import { RigModel, band, ellipseOps, hsl, idHash, mix, scale, WHITE, type Animator, type Form, type Palette, type Part, type PathOp, type Pose, type Shape } from "./Rig";
import { pixelModel } from "./pixelArt";

/**
 * Gas bags: a beast that floats on a great bladder of gas on its back, like
 * a hot-air balloon with a creature for a basket. Under it hangs the beast,
 * head forward with a big eye and a mouth for leaves, fins at its sides
 * that paddle it along. Two kinds, by id:
 * - airship: a ribbed, mottled bladder, shaded under and shining on top,
 *   with a tail fin at its back to steer;
 * - lantern: a thin see-through bell with the warm gas glowing inside,
 *   veined like a leaf, puffing from a vent at its back to steer.
 * The bladder's middle is the origin; it faces +x.
 */

export type GasbagVariant = "airship" | "lantern";
export const GASBAG_VARIANTS: readonly GasbagVariant[] = ["airship", "lantern"];

export function gasbagVariant(id: string): GasbagVariant {
  return idHash(id, 5) < 0.5 ? "airship" : "lantern";
}

/** The bladder's half width and half height (GASBAGS.radius is 3). */
export const BA = 3.75;
export const BB = 2.4;

const FIN: Form = { kind: "path", ops: [["M", 0, 0], ["Q", -0.6, 0.2, -1.1, -0.45], ["Q", -0.5, -0.35, 0, -0.15], ["Z"]] };

/** The beast slung under the bladder: a squat body, a fin each side, a head forward with a big eye and a jaw that munches. */
const BEAST: Part[] = [
  { name: "beast", parent: "root", at: [0.2, -BB * 0.92], z: 4, shapes: [{ color: "hide", form: { kind: "path", ops: [["M", 1.5, 0], ["C", 1.4, 0.7, -1.2, 0.75, -1.7, 0.15], ["C", -1.6, -0.55, 0.9, -0.85, 1.5, 0], ["Z"]] } }] },
  { name: "farFin", parent: "beast", at: [-0.6, -0.05], z: 3, moves: true, shapes: [{ color: "dark", form: FIN }] },
  { name: "nearFin", parent: "beast", at: [-0.3, -0.05], z: 5, moves: true, shapes: [{ color: "hide", form: FIN }] },
  {
    name: "head",
    parent: "beast",
    at: [1.3, 0.15],
    z: 7,
    moves: true,
    shapes: [
      { color: "hide", form: { kind: "path", ops: [["M", -0.3, 0.45], ["C", 0.4, 0.62, 0.95, 0.35, 1.05, 0], ["L", 0.35, -0.12], ["C", 0.1, -0.4, -0.4, -0.35, -0.45, 0], ["Z"]] } },
      { color: "white", at: [0.35, 0.2], form: { kind: "disc", r: 0.17 } },
      { color: "eye", at: [0.39, 0.19], form: { kind: "disc", r: 0.09 } },
      { color: "dark", at: [0.3, 0.36], form: { kind: "disc", r: 0.22, ry: 0.06 } },
    ],
  },
  { name: "jaw", parent: "head", at: [0.35, -0.1], z: 6, moves: true, shapes: [{ color: "dark", form: { kind: "path", ops: [["M", 0, 0], ["L", 0.68, 0.04], ["C", 0.5, -0.25, 0.1, -0.3, -0.05, -0.12], ["Z"]] } }] },
];

/** The airship bladder: hoops round it like an airship's, each bowed a little forward, and blotches placed by the id. */
function airship(id: string): Part[] {
  const ribs: Shape[] = [-0.66, -0.33, 0, 0.33, 0.66].map((k) => {
    const h = BB * Math.sqrt(1 - k * k) * 0.97;
    const ops: PathOp[] = [];
    for (let i = 0; i <= 12; i++) {
      const a = -Math.PI / 2 + (i / 12) * Math.PI;
      ops.push([i ? "L" : "M", BA * k + BA * 0.07 * Math.cos(a), h * Math.sin(a)]);
    }
    return { color: "rib", form: { kind: "stroke", width: 0.05, ops } };
  });
  const blotches: Shape[] = Array.from({ length: 6 }, (_, i) => {
    const a = idHash(id, 10 + i) * Math.PI;
    const r = 0.25 + idHash(id, 20 + i) * 0.55;
    return { color: "blotch", at: [Math.cos(a) * BA * r, Math.sin(a) * BB * r * 0.8], form: { kind: "disc", r: 0.18 + idHash(id, 30 + i) * 0.25, ry: 0.12 + idHash(id, 40 + i) * 0.15 } };
  });
  return [
    { name: "tailFin", parent: "root", at: [-BA * 0.92, BB * 0.1], z: 0, moves: true, shapes: [{ color: "shade", form: { kind: "path", ops: [["M", 0, 0.4], ["Q", -0.8, 0.9, -1.3, 1], ["Q", -1, 0, -1.3, -1], ["Q", -0.8, -0.9, 0, -0.4], ["Z"]] } }] },
    {
      name: "bladder",
      parent: "root",
      at: [0, 0],
      z: 1,
      moves: true,
      shapes: [{ color: "skin", form: { kind: "disc", r: BA, ry: BB } }, { color: "shade", form: band(BA, BB, 0.35, false) }, { color: "shine", at: [0, BB * 0.08], form: band(BA * 0.8, BB * 0.82, 0.55, true) }, ...ribs, ...blotches],
    },
  ];
}

/** The lantern bell: the gas glowing inside a see-through membrane veined from a knot on top, a vent at the back puffing to steer. */
function lantern(): Part[] {
  const veins: PathOp[] = [];
  for (let i = -3; i <= 3; i++) {
    const x1 = BA * 0.95 * Math.sin(i * 0.42);
    const y1 = -BB * 0.9 * Math.cos(i * 0.42);
    veins.push(["M", i * 0.15, BB * 0.85], ["Q", i * 0.9, BB * 0.25, x1, y1], ["M", x1 * 0.6, (y1 + BB * 0.4) * 0.6], ["L", x1 * 0.6 + Math.sign(i || 1) * 0.5, (y1 + BB * 0.4) * 0.6 - 0.5]);
  }
  const puff = (name: string): Part => ({ name, parent: "root", at: [-BA - 0.5, BB * 0.15], z: 0, moves: true, shapes: [{ color: "gas", alpha: 0.5, form: { kind: "disc", r: 0.3 } }] });
  return [
    puff("puff0"),
    puff("puff1"),
    { name: "gas", parent: "root", at: [0, -0.5], z: 1, shapes: [{ color: "gas", alpha: 0.7, form: { kind: "disc", r: 2.7, ry: 1.5 } }] },
    {
      name: "bladder",
      parent: "root",
      at: [0, 0],
      z: 2,
      moves: true,
      shapes: [
        { color: "membrane", alpha: 0.35, form: { kind: "disc", r: BA, ry: BB } },
        { color: "rim", alpha: 0.9, form: { kind: "stroke", width: 0.07, ops: ellipseOps(0, 0, BA, BB) } },
        { color: "rib", alpha: 0.7, form: { kind: "stroke", width: 0.04, ops: veins } },
        { color: "white", alpha: 0.35, at: [0, BB * 0.1], form: band(BA * 0.75, BB * 0.8, 0.6, true) },
        { color: "shade", at: [0, BB * 0.95], form: { kind: "disc", r: 0.22, ry: 0.14 } },
      ],
    },
  ];
}

export function gasbagModel(id: string, variant = gasbagVariant(id)): RigModel {
  return pixelModel({ parts: [{ name: "root", at: [0, 0], z: 0, moves: true, shapes: [] }, ...(variant === "airship" ? airship(id) : lantern()), ...BEAST], glow: ["gas"] });
}

/** Bladders from cream to peach to sage, the beast a warm grey-brown; the lantern's gas a warm glow. */
export function gasbagPalette(id: string): Palette {
  const hue = [0.08, 0.12, 0.03, 0.22][Math.floor(idHash(id, 1) * 4)]! + idHash(id, 2) * 0.03;
  const skin = hsl(hue, 0.42, 0.7);
  const hide = hsl(0.07 + idHash(id, 3) * 0.04, 0.22, 0.32 + idHash(id, 4) * 0.08);
  return {
    skin,
    shade: scale(skin, 0.72),
    shine: mix(skin, WHITE, 0.5),
    rib: scale(skin, 0.6),
    blotch: hsl(hue + 0.02, 0.47, 0.52),
    hide,
    dark: scale(hide, 0.65),
    white: [0.96, 0.94, 0.89],
    eye: [0.07, 0.05, 0.03],
    membrane: mix(skin, WHITE, 0.3),
    rim: mix(skin, WHITE, 0.5),
    gas: [1.4, 1.0, 0.5],
  };
}

export interface Drift {
  /** How fast it's going, as a share of its top speed (0..1). */
  going: number;
  eating: boolean;
}

/** Bobbing as it drifts, breathing (gulping as it eats), fins paddling faster the faster it goes, jaw munching at the leaves, the vent puffing. */
export class GasbagAnimator implements Animator<Drift> {
  private t: number;

  constructor(seed = Math.random()) {
    this.t = seed * 20;
  }

  step(dt: number, g: Drift): Pose {
    this.t += dt;
    const t = this.t;
    const gulp = g.eating ? Math.sin(t * 4) * 0.035 : Math.sin(t * 1.2) * 0.015;
    const beat = t * (2 + g.going * 3);
    const puff = (i: number) => {
      const k = (((t * 0.7 + i * 0.5) % 1) + 1) % 1;
      const s = (0.5 + k * 1.2) * (1 - k * 0.7);
      return { x: -k * 1.6, y: Math.sin(t * 3 + i) * 0.15, sx: s, sy: s };
    };
    return {
      root: { y: Math.sin(t * 0.9) * 0.35 },
      bladder: { sx: 1 + gulp, sy: 1 - gulp * 0.6 },
      tailFin: { rot: Math.sin(t * 1.1) * 0.15 },
      farFin: { rot: Math.sin(beat) * (0.25 + g.going * 0.25) },
      nearFin: { rot: Math.sin(beat + 1.3) * (0.25 + g.going * 0.25) },
      head: { rot: g.eating ? 0.15 + Math.sin(t * 2.5) * 0.08 : Math.sin(t * 0.7) * 0.05 },
      jaw: { rot: g.eating ? -Math.max(0, Math.sin(t * 7)) * 0.45 : 0 },
      puff0: puff(0),
      puff1: puff(1),
    };
  }
}
